import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation, useBlocker } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { addOpenSection } from "../../../../Redux/Slices/OpenSection/AddOpenSectionSlice";
import { listInstructionExamID, clearlistInstructionExamID } from "../../../../Redux/Slices/Exam/ListinstructionExamIDSlice";
import { listExamID, clearListExamID } from "../../../../Redux/Slices/Exam/ListExamIDSlice";
import { listStudentExam, clearlistStudentExam } from "../../../../Redux/Slices/StudentExam/ListStudentExamSlice";
import { pendingExam, clearpendingExam } from "../../../../Redux/Slices/PendingExam/PendingExamSlice";
import { pendingPracticeExam, clearpendingPracticeExam } from "../../../../Redux/Slices/PendingPracticeExam/PendingPracticeExamSlice";
import { clearListReExamID } from "../../../../Redux/Slices/ReExam/ListReExamSlice";
import { clearListReExamPracticeID } from "../../../../Redux/Slices/ReExamPractice/ListReExamPracticeSlice";
import CircularProgress from "@mui/material/CircularProgress";
import { UcatLayout } from "../../Components/Shared";
import { ReloadWarningModal, ScrollWarningModal } from "../../Components/Practice_Test/Exam_component";
import useNavigationBlocker from "../../../../Hooks/useNavigationBlocker";
import { Clock } from "lucide-react";

// Helper function to parse time string
const parsePendingTime = (timeStr: string | undefined): number | null => {
  if (!timeStr) return null;

  // Handle "MM:SS" format
  if (timeStr.includes(':')) {
    const parts = timeStr.split(':');
    if (parts.length === 2) {
      const mins = parseInt(parts[0], 10);
      const secs = parseInt(parts[1], 10);
      return (mins * 60) + secs;
    }
    if (parts.length === 3) {
      const hours = parseInt(parts[0], 10);
      const mins = parseInt(parts[1], 10);
      const secs = parseInt(parts[2], 10);
      return (hours * 3600) + (mins * 60) + secs;
    }
  }

  // Handle "X minutes" or "X seconds"
  const match = timeStr.match(/(\d+)/);
  if (match) {
    const val = parseInt(match[0], 10);
    if (timeStr.includes('minute')) return val * 60;
    if (timeStr.includes('hour')) return val * 3600;
    return val;
  }

  return null;
};

export const UcatInstructionsScreen: React.FC = () => {
  const navigate = useNavigate();
  useNavigationBlocker(true);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { exam_id: stateExamId, sectionIndex } = location.state || {}; // Get exam_id and sectionIndex
  console.log('TestInstructionScreen: location.state on load:', location.state);
  console.log('TestInstructionScreen: location.state keys:', location.state ? Object.keys(location.state) : 'null');

  const examMode = location.state?.examMode || sessionStorage.getItem("ucat_exam_mode");
  console.log('TestInstructionScreen: sessionStorage values on load:', {
    ucat_exam_mode: sessionStorage.getItem("ucat_exam_mode"),
    exam_mode: sessionStorage.getItem("exam_mode"),
    resume_exam: sessionStorage.getItem("resume_exam"),
    location_state_examMode: location.state?.examMode,
    final_examMode: examMode
  });

  const effectiveExamId = stateExamId || (sessionStorage.getItem("ucat_exam_id") ? parseInt(sessionStorage.getItem("ucat_exam_id")!) : undefined);

  // Get ReExam Data
  const { data: reExamSlice, isLoading: isReExamLoading } = useAppSelector(
    (state) => (state as any).ListReExamIDSlice
  );
  const reExamData = reExamSlice?.data;

  const { data: reExamPracticeSlice, isLoading: isReExamPracticeLoading } = useAppSelector(
    (state) => (state as any).ListReExamPracticeIDSlice
  );
  const reExamPracticeData = Array.isArray(reExamPracticeSlice?.data)
    ? reExamPracticeSlice?.data[0]
    : reExamPracticeSlice?.data;

  const { data: listInstructionExamSlice, isLoading: isInstructionLoading } = useAppSelector(
    (state) => state.ListInstructionExamSlice
  );

  const { data: addStudentExamSlice } = useAppSelector(
    (state) => state.AddStudentExamSlice
  );

  const { data: listStudentExamSlice, isLoading: isStudentExamLoading } = useAppSelector(
    (state) => state.ListStudentExamSlice
  );

  // Robust studentExamId resolution
  const studentExamId = React.useMemo(() => {
    // 1. Prioritize navigation state (Explicitly passed)
    if (location.state?.student_exam_id) return location.state.student_exam_id;

    // 2. Fresh API response from creation (The ID we just got)
    const addId = (addStudentExamSlice as any)?.data?.id || (addStudentExamSlice as any)?.id;
    if (addId) return addId;

    // 3. Session storage (Robust for reloads)
    const sessionId = sessionStorage.getItem("ucat_student_exam_id");
    if (sessionId) return sessionId;

    // 4. Fallback to Latest from List
    const data = listStudentExamSlice?.data;
    if (Array.isArray(data) && data.length > 0) {
      return data[0]?.student_exam_id || data[0]?.id;
    }
    return data?.student_exam_id || data?.id;
  }, [location.state?.student_exam_id, addStudentExamSlice, listStudentExamSlice]);

  const { data: listExamIDSlice, isLoading: isListExamLoading } = useAppSelector(
    (state) => state.ListExamIDSlice
  );

  const { data: pendingExamSlice, isLoading: isPendingLoading } = useAppSelector(
    (state) => state.PendingExamSlice
  );

  const { data: pendingPracticeExamSlice, isLoading: isPendingPracticeLoading } = useAppSelector(
    (state) => state.PendingPracticeExamSlice
  );

  const pendingData = Array.isArray(pendingExamSlice?.data)
    ? pendingExamSlice?.data[0]
    : pendingExamSlice?.data ||
    (Array.isArray(pendingPracticeExamSlice?.data)
      ? pendingPracticeExamSlice?.data[0]
      : pendingPracticeExamSlice?.data);

  // Dispatch Pending Practice Exam if needed
  useEffect(() => {
    if (examMode === 'pending' && studentExamId && !pendingExamSlice?.data) {
      dispatch(pendingPracticeExam({ id: studentExamId }));
    }
  }, [examMode, studentExamId, dispatch, pendingExamSlice]);

  // Determine examData (instructions) based on mode
  let examData = listInstructionExamSlice?.data || listExamIDSlice?.data; // Fallback to listExamIDSlice if instruction slice is empty

  if (examMode === 'retake') {
    if (reExamData) examData = reExamData;
    else if (reExamPracticeData) examData = reExamPracticeData;
  } else if (examMode === 'pending') {
    // For pending exams, prioritize resume API data over start API data
    if (pendingData) {
      examData = pendingData;
      console.log('TestInstructionScreen: Using pending exam data from ResumeExam API');
    } else {
      console.log('TestInstructionScreen: No pending data available, using fallback');
    }
  } else if (pendingData || reExamData || reExamPracticeData) {
    // Fallback
    examData = pendingData || reExamData || reExamPracticeData || examData;
  }

  // Determine examSubData (questions/sections) based on mode
  let examSubData: any = listExamIDSlice?.data;
  if (examMode === 'retake') {
    if (reExamData?.questions_by_type) examSubData = reExamData;
    else if (reExamPracticeData?.questions_by_type) examSubData = reExamPracticeData;
  } else if (examMode === 'pending') {
    // For pending exams, prioritize resume API data over start API data
    if (pendingData?.questions_by_type) {
      examSubData = pendingData;
      console.log('TestInstructionScreen: Using pending exam subdata from ResumeExam API');
    } else {
      console.log('TestInstructionScreen: No pending subdata available, using fallback');
    }
  } else if (reExamData?.questions_by_type || pendingData?.questions_by_type || reExamPracticeData?.questions_by_type) {
    // Fallback
    examSubData = (reExamData?.questions_by_type && reExamData) ||
      (pendingData?.questions_by_type && pendingData) ||
      (reExamPracticeData?.questions_by_type && reExamPracticeData);
  }

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [showReloadWarning, setShowReloadWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(90); // Default to 120, will be updated based on sectionIndex
  const [showScrollWarning, setShowScrollWarning] = useState(false);
  const [scrollStatus, setScrollStatus] = useState({ left: true, right: true });
  const scrollRef = useRef<HTMLDivElement>(null);



  // Determine current sectionIndex, prioritizing location state but falling back to storage
  const isSectionInstruction = location.pathname === "/ucat_exampractice_instruction";
  const effectiveSectionIndex = isSectionInstruction
    ? (sectionIndex !== undefined && sectionIndex !== null
      ? sectionIndex
      : (sessionStorage.getItem("ucat_last_section_index") ? parseInt(sessionStorage.getItem("ucat_last_section_index")!) : undefined))
    : undefined;
  const sections = examSubData?.questions_by_type || [];
  console.log('TestInstructionScreen: sections created from examSubData:', {
    examMode,
    examSubData,
    sections,
    questions_by_type: examSubData?.questions_by_type
  });

  // Robust section identification
  const currentRawSection = (() => {
    if (!isSectionInstruction) return null;

    // 1. Try matching by EXPLICIT ID in navigation state
    if (location.state?.question_type_id) {
      const found = sections.find((s: any) => s.question_type_id === location.state.question_type_id);
      if (found) return found;
    }

    // 2. If we are explicitly navigating with an index, trust that index
    if (location.state?.sectionIndex !== undefined && sections.length > location.state.sectionIndex) {
      return sections[location.state.sectionIndex];
    }

    // 3. Only if it's a cold reload (no state), try matching by stored ID
    if (!location.state) {
      const storedId = sessionStorage.getItem("ucat_last_section_id") ? parseInt(sessionStorage.getItem("ucat_last_section_id")!) : undefined;
      if (storedId) {
        const found = sections.find((s: any) => s.question_type_id === storedId);
        if (found) return found;
      }
    }

    // 4. Fallback to absolute index (if using full list)
    if (sections.length > effectiveSectionIndex!) {
      return sections[effectiveSectionIndex!];
    }

    // 5. Final fallback: if resuming and sections has items, take the first one
    if ((pendingData || reExamData) && sections.length > 0) {
      return sections[0];
    }

    return null;
  })();

  const readTime = React.useMemo(() => {
    if (!isSectionInstruction) return 120; // General instructions default

    if (currentRawSection) {
      const sec: any = currentRawSection;
      // Check for specific instruction timing fields
      if (sec.instruction_time) {
        const parsed = parsePendingTime(String(sec.instruction_time));
        if (parsed) return parsed;
      }
      if (sec.instruction_duration) {
        const parsed = parsePendingTime(String(sec.instruction_duration));
        if (parsed) return parsed;
      }
    }
    return 60; // Default 1 min for sections
  }, [isSectionInstruction, currentRawSection]);

  const scrollStatusRef = useRef(scrollStatus);

  // Update ref when scrollStatus changes
  useEffect(() => {
    scrollStatusRef.current = scrollStatus;
  }, [scrollStatus]);

  // SessionStorage key for timer persistence
  const getStorageKey = useCallback(() => {
    const baseKey = 'ucat_instruction_timer';
    if (effectiveExamId) {
      return `${baseKey}_${effectiveExamId}${sectionIndex !== undefined && sectionIndex !== null ? `_section_${sectionIndex}` : ''}`;
    }
    return baseKey;
  }, [effectiveExamId, sectionIndex]);

  // Effects
  useEffect(() => {
    if (stateExamId) {
      sessionStorage.setItem("ucat_exam_id", stateExamId.toString());
    }
  }, [stateExamId]);

  useEffect(() => {
    const storageKey = getStorageKey();

    // Clear old timers and add current subtype recent timer
    if (currentRawSection?.question_type_id) {
      const subtypeId = currentRawSection.question_type_id;

      // Clear all old ucat instruction timers from storage
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('ucat_instruction_timer_')) {
          sessionStorage.removeItem(key);
        }
      });

      // Add current subtype recent timer to storage
      const recentTimerKey = `ucat_recent_timer_subtype_${subtypeId}`;
      const timerData = {
        timeLeft: readTime,
        timestamp: Date.now(),
        subtype_id: subtypeId,
        subtype_name: currentRawSection.question_type_name
      };
      sessionStorage.setItem(recentTimerKey, JSON.stringify(timerData));
    }

    // If explicitly asked to reset timer (e.g. from previous section end), clear storage
    if (location.state?.resetTimer) {
      sessionStorage.removeItem(storageKey);
    }

    const storedData = sessionStorage.getItem(storageKey);

    if (storedData) {
      try {
        const { timeLeft: storedTime, timestamp } = JSON.parse(storedData);
        const elapsedSeconds = Math.floor((Date.now() - timestamp) / 1000);
        const remainingTime = Math.max(0, storedTime - elapsedSeconds);

        if (remainingTime <= 0) {
          setTimeLeft(0);
        } else {
          setTimeLeft(remainingTime);
        }
      } catch (error) {
        console.warn('Failed to parse stored timer data:', error);
        setTimeLeft(readTime);
      }
    } else {
      setTimeLeft(readTime);
    }
  }, [getStorageKey, readTime, location.state?.resetTimer, currentRawSection]);

  // Persist sectionIndex and ID to handle reloads
  useEffect(() => {
    if (sectionIndex !== undefined && sectionIndex !== null) {
      sessionStorage.setItem("ucat_last_section_index", sectionIndex.toString());
    }
  }, [sectionIndex]);

  useEffect(() => {
    if (currentRawSection?.question_type_id) {
      sessionStorage.setItem("ucat_last_section_id", currentRawSection.question_type_id.toString());
    }
  }, [currentRawSection?.question_type_id]);

  // Save timer state to sessionStorage
  useEffect(() => {
    const storageKey = getStorageKey();
    if (timeLeft > 0) {
      const timerData = { timeLeft, timestamp: Date.now() };
      sessionStorage.setItem(storageKey, JSON.stringify(timerData));
    } else {
      sessionStorage.removeItem(storageKey);
    }
  }, [timeLeft, getStorageKey]);

  useEffect(() => {
    if (effectiveExamId) {
      // Clear stale data to prevent "flashing" old exam content
      dispatch(clearlistInstructionExamID());
      dispatch(clearListExamID());
      dispatch(clearlistStudentExam());

      if (!examMode || examMode === 'new') {
        dispatch(clearpendingExam());
        dispatch(clearpendingPracticeExam());
        dispatch(clearListReExamID());
        dispatch(clearListReExamPracticeID());
        dispatch(listInstructionExamID({ exam_id: effectiveExamId }));
      }
      dispatch(listStudentExam());
    }
  }, [dispatch, effectiveExamId, examMode]);

  useEffect(() => {
    if (studentExamId) {
      console.log('TestInstructionScreen: examMode =', examMode, 'studentExamId =', studentExamId);

      // Only call listExamID (start API) for new exams, not for pending exams
      if (examMode === 'new') {
        console.log('TestInstructionScreen: Calling start API for new exam');
        dispatch(listExamID({ stud_exam_id: studentExamId }));
      } else if (examMode === 'retake') {
        console.log('TestInstructionScreen: Calling start API for retake exam');
        dispatch(listExamID({ stud_exam_id: studentExamId }));
      } else if (examMode === 'pending') {
        console.log('TestInstructionScreen: Skipping start API for pending exam');
      } else {
        console.log('TestInstructionScreen: examMode is null/undefined, calling start API as fallback');
        dispatch(listExamID({ stud_exam_id: studentExamId }));
      }

      if (examMode === 'pending') {
        console.log('TestInstructionScreen: Calling resume API for pending exam');
        dispatch(pendingExam({ id: Number(studentExamId) }));
      } else if (examMode === 'new' && (!examSubData || !examSubData.questions_by_type)) {
        console.log('TestInstructionScreen: Calling resume API as fallback for new exam');
        dispatch(pendingExam({ id: Number(studentExamId) }));
      } else if (examMode === 'retake' && (!examSubData || !examSubData.questions_by_type)) {
        console.log('TestInstructionScreen: Calling resume API as fallback for retake exam');
        dispatch(pendingExam({ id: Number(studentExamId) }));
      }
    }
  }, [dispatch, studentExamId, examMode]);

  const checkScrollStatus = useCallback(() => {
    const buffer = 10;
    let newRight = scrollStatusRef.current.right;

    if (!newRight && scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (
        scrollHeight <= clientHeight ||
        scrollTop + clientHeight >= scrollHeight - buffer
      ) {
        newRight = true;
      }
    }

    if (newRight !== scrollStatusRef.current.right) {
      setScrollStatus({ left: true, right: newRight });
    }
  }, []);

  const handleScroll = useCallback(() => {
    checkScrollStatus();
  }, [checkScrollStatus]);

  // Check initial scroll status on mount
  useEffect(() => {
    const checkInitial = () => {
      let r = true;
      if (scrollRef.current && scrollRef.current.scrollHeight > scrollRef.current.clientHeight + 5) {
        r = false;
      }
      return { left: true, right: r };
    };

    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    const initial = checkInitial();
    setScrollStatus(initial);

    const timeoutId = setTimeout(() => {
      setScrollStatus(checkInitial());
    }, 300);

    const handleResize = () => {
      if (scrollRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const buffer = 10;
        const newRight = scrollHeight <= clientHeight || scrollTop + clientHeight >= scrollHeight - buffer;

        if (newRight !== scrollStatusRef.current.right) {
          setScrollStatus({ left: true, right: newRight });
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Pearson-style browser back-button trap
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setShowReloadWarning(true);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isSubmitting) return;
      e.preventDefault();
      e.returnValue = "";
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "F5" || ((e.ctrlKey || e.metaKey) && (e.key === "r" || e.key === "R"))) {
        e.preventDefault();
        setShowReloadWarning(true);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSubmitting]);

  const blocker = useBlocker(
    ({ historyAction }) => {
      if (isSubmittingRef.current) return false;
      if (historyAction === "POP") return true; // Block back button
      return false; // Allow programmatic navigation
    }
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowReloadWarning(true);
    } else {
      setShowReloadWarning(false);
    }
  }, [blocker.state]);

  const handleReloadConfirm = () => {
    if (blocker.state === "blocked") {
      blocker.proceed();
    } else {
      window.location.reload();
    }
    setShowReloadWarning(false);
  };

  const handleReloadCancel = () => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setShowReloadWarning(false);
  };

  console.log("DEBUG: TestInstructionScreen", {
    sectionIndex,
    effectiveSectionIndex,
    totalSections: sections.length,
    currentRawSectionId: currentRawSection?.question_type_id,
    currentRawSectionName: currentRawSection?.question_type_name
  });

  const handleNext = useCallback(() => {
    if (timeLeft <= 0) {
      isSubmittingRef.current = true;
      setIsSubmitting(true);

      const targetPath = "/ucat_exam_practice";

      let pendingTimeVal = null;
      if (examMode === 'pending') {
        const serverData = pendingData;
        pendingTimeVal = serverData?.pending_time;
      }

      if (isSectionInstruction) {
        // Proceed to the actual exam section
        if (studentExamId && currentRawSection) {
          console.log("🚀 Opening Section:", currentRawSection.question_type_id);
          dispatch(
            addOpenSection({
              student_exam_id: studentExamId,
              remaining_time: null,
              closed: false,
              question_type_id: currentRawSection.question_type_id,
            })
          );
        }
        navigate(targetPath, {
          state: {
            ...location.state,
            student_test_id: studentExamId, // Explicitly pass this
            exam_id: effectiveExamId,
            sectionIndex: effectiveSectionIndex,
            question_type_id: currentRawSection?.question_type_id,
            pending_time: pendingTimeVal,
            startNow: (!examMode || examMode === 'new'), // Default to startNow if new
            isResume: (examMode === 'pending'),
            isRetake: (examMode === 'retake')
          },
        });
      } else {
        // Proceed from General Instructions to the first Subtest's Instructions
        navigate("/ucat_exampractice_instruction", {
          replace: true,
          state: {
            ...location.state,
            student_test_id: studentExamId,
            exam_id: effectiveExamId,
            sectionIndex: 0,
            question_type_id: sections[0]?.question_type_id
          },
        });
      }
      return;
    }

    if (!scrollStatus.left || !scrollStatus.right) {
      setShowScrollWarning(true);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const targetPath = "/ucat_exam_practice";

    if (isSectionInstruction) {
      // Proceed to the actual exam section
      if (studentExamId && currentRawSection) {
        console.log("🚀 Opening Section:", currentRawSection.question_type_id);
        dispatch(
          addOpenSection({
            student_exam_id: studentExamId,
            remaining_time: null,
            closed: false,
            question_type_id: currentRawSection.question_type_id,
          })
        );
      }
      navigate(targetPath, {
        state: {
          ...location.state,
          student_test_id: studentExamId,
          exam_id: effectiveExamId,
          sectionIndex: effectiveSectionIndex,
          question_type_id: currentRawSection?.question_type_id,
          startNow: location.state?.startNow || (!examMode || examMode === 'new'),
          isResume: (examMode === 'pending'),
          isRetake: (examMode === 'retake')
        },
      });
    } else {
      // Proceed from General Instructions to the first Subtest's Instructions
      let targetIndex = 0;
      let targetSectionId = sections[0]?.question_type_id;

      // If resuming (pending mode), try to find the last active section
      if (examMode === 'pending') {
        const serverData = pendingData;
        const pendingSectionId = serverData?.question_type_id;

        if (pendingSectionId) {
          const idx = sections.findIndex((s: any) => s.question_type_id === pendingSectionId);
          if (idx !== -1) {
            targetIndex = idx;
            targetSectionId = pendingSectionId;
            console.log("Resume: Found active section", targetIndex, targetSectionId);
          }
        }
      }

      navigate("/ucat_exampractice_instruction", {
        replace: true,
        state: {
          ...location.state,
          student_test_id: studentExamId,
          exam_id: effectiveExamId,
          sectionIndex: targetIndex,
          question_type_id: targetSectionId,
          startNow: location.state?.startNow || (!examMode || examMode === 'new'),
          isResume: (examMode === 'pending'),
          isRetake: (examMode === 'retake')
        },
      });
    }
  }, [timeLeft, isSectionInstruction, studentExamId, currentRawSection, dispatch, navigate, effectiveExamId, effectiveSectionIndex, scrollStatus, examMode]); // Added examMode dep

  // Timer logic for the instruction screen
  useEffect(() => {
    if (timeLeft <= 0) {
      handleNext();
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, handleNext]);

  const handleStartSection = () => {
    handleNext();
  };

  // Keyboard shortcuts handler
  const handleKeyDown = (e: KeyboardEvent) => {
    // Only handle Alt key combinations or modal-specific shortcuts
    if (e.altKey) {
      const code = e.code;

      // Prevent default browser behavior
      e.preventDefault();

      switch (code) {
        case 'KeyN':
          handleStartSection();
          break;
      }
    } else if (showReloadWarning) {
      // Handle ReloadWarningModal shortcuts
      const key = e.key.toLowerCase();
      if (key === 'r') {
        e.preventDefault();
        handleReloadConfirm();
      } else if (key === 'c') {
        e.preventDefault();
        handleReloadCancel();
      }
    }
  };

  // React useEffect with proper cleanup
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup on unmount
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, showScrollWarning, handleStartSection]);

  const isApiLoading = isReExamLoading || isReExamPracticeLoading || isInstructionLoading || isStudentExamLoading || isListExamLoading || isPendingLoading || isPendingPracticeLoading;

  const isDataIncomplete = isSectionInstruction
    ? (!currentRawSection || (currentRawSection as any).question_count === 0)
    : (!sections || sections.length === 0);

  if (!examData || (isApiLoading && isDataIncomplete)) {
    return (
      <div className="flex h-screen items-center justify-center bg-white flex-col gap-3">
        <CircularProgress />
        <p className="text-gray-500 font-medium">Loading Instructions...</p>
      </div>
    );
  }

  const getSectionInstructions = () => {
    if (!currentRawSection) return [];

    // Check for instructions directly on the section or on the first question
    // Using any to handle potential array format from backend
    const rawInst: any = (currentRawSection as any).instructions || currentRawSection.questions?.[0]?.instruction;

    if (Array.isArray(rawInst)) {
      return rawInst.map((text: string) => ({ instruction: text }));
    }
    return rawInst ? [{ instruction: rawInst }] : [];
  };

  const formatTime = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const instructions = isSectionInstruction
    ? getSectionInstructions()
    : (Array.isArray(examData.exam_instructions)
      ? examData.exam_instructions.map((inst: any) =>
        typeof inst === 'string' ? { instruction: inst } : inst
      )
      : []);

  const displayedSections = isSectionInstruction && currentRawSection
    ? [currentRawSection]
    : sections;

  return (
    <UcatLayout
      headerTitle={isSectionInstruction ? (currentRawSection?.question_type_name ?? "") : (examData.exam_name ?? "")}
      headerSubtitle={
        <div className={`flex items-center gap-1 text-[16px] mt-[-2px] text-[#FFFF00]`}>
          <Clock size={16} strokeWidth={2.5} className="mb-0.5" />

          <span
            className="f-tahoma"
            style={{
              color: timeLeft <= 30 ? "#FF0" : "#FF0",
              fontSize: "inherit"
            }}
          >
            Time remaining: {formatTime(timeLeft)}
          </span>
        </div>

      }
      showToolbar={true}
      isLoading={isApiLoading}
      footerProps={{
        onNext: handleStartSection,
        rightButtonText: "Next",
        leftButtonText: "",
        nextDisabled: isApiLoading // Disable next button while loading
      }}
    >
      {/* ================= SCROLLABLE CONTENT ================= */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto min-h-0 p-4 text-black text-[15px] leading-[1.2] select-none font-[Arial,sans-serif]"
      >
        <div className="max-w-full" style={{ fontSize: "15px" }}>
          <p className="font-bold mb-4 text-[15px]">
            {/* You have {readTime % 60 === 0 ? Math.floor(readTime / 60) : (readTime / 60).toFixed(1)} minutes to read this screen. */}
            The time allocated for reading these instructions is in the top right of the screen. When the time expires the subtest will begin.
          </p>

          <p className="mb-4 text-[15px]">
            {isSectionInstruction
              ? `This subtest consists of ${currentRawSection?.question_count || 0} questions:`
              : `This exam consists of ${sections.length} subtests:`}
          </p>

          <div className="overflow-x-auto mb-8 text-[15px]">
            <table className="w-full border-collapse border border-gray-800 text-[14px] max-w-4xl text-[15px]">
              <thead>
                <tr>
                  <th className="border border-gray-800 p-3 text-center w-1/3 font-bold text-[15px] bg-[#fff]" rowSpan={2}>Subtest</th>
                  <th className="border border-gray-800 p-3 text-center w-1/12 font-bold text-[15px] bg-[#fff]" rowSpan={2}>Number of Questions</th>
                  <th className="border border-gray-800 p-1 text-center font-bold text-[15px] bg-[#fff]" colSpan={2}>Subtest Time</th>
                </tr>
                <tr>
                  <th className="border border-gray-800 p-2 text-center text-[12px] font-bold text-[15px] bg-[#fff]">UCAT or UCAT_ANZ</th>
                  <th className="border border-gray-800 p-2 text-center text-[12px] font-bold text-[15px] bg-[#fff]">UCATSEN or UCAT_ANZ_SEN</th>
                </tr>
              </thead>
              <tbody>
                {displayedSections.map((section: any, index: number) => (
                  <tr key={index}>
                    <td className="border border-gray-800 p-[4px] font-bold text-left text-[15px]">{section.question_type_name}</td>
                    <td className="border border-gray-800 p-[4px] text-center text-[15px]">{section.question_count}</td>
                    <td className="border border-gray-800 p-[4px] text-center text-[15px]">{section.duration || "N/A"}</td>
                    <td className="border border-gray-800 p-[4px] text-center text-[15px]">
                      {section.duration ? `${Math.ceil(parseInt(section.duration) * 1.25)} mins` : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mt-10 text-[15px]">
            <h3 className="font-bold text-[16px] underline decoration-1 underline-offset-4 text-[15px]">INSTRUCTIONS</h3>
            <div className="space-y-3 text-[15px]">
              {instructions.length > 0 ? (
                instructions.map((inst: any, idx: number) => (
                  <div key={idx} className="flex gap-2 text-[15px]">
                    <span className="font-bold text-[15px]"></span>
                    <p className=" text-[15px]">{inst.instruction}</p>
                  </div>
                ))
              ) : (
                <p className=" text-[15px]">{examData.exam_description}</p>
              )}
            </div>

            <p className=" text-[15px] pt-4">
              Please click the <b style={{ fontSize: "inherit" }}>Next (N)</b> button to proceed before the time on this screen expires.
            </p>
          </div>
        </div>
      </div>
      <ScrollWarningModal
        isOpen={showScrollWarning}
        onClose={() => setShowScrollWarning(false)}
      />
      <ReloadWarningModal
        isOpen={showReloadWarning}
        onClose={handleReloadCancel}
        onConfirm={handleReloadConfirm}
      />
    </UcatLayout>
  );
};
